import { TrailerDialogComponent } from '@/components/dialogs/trailer-dialog/trailer-dialog';
import { MovieItemComponent } from '@/components/movie-item/movie-item';
import { PlayButton } from '@/components/play-button/play-button';
import { WatchListIconComponent } from '@/components/watch-list-icon/watch-list-icon';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import { cn } from '@/lib/utils';
import { MoviePoster } from '@/types/movie-poster';
import { BasicShowtime } from '@/types/showtimes';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { hasAdvanceTickets } from '@/utils/movie-helper';
import { useState } from 'react';
import { Anchor } from '../ui/anchor';

type MoviePosterComponentProps = {
  dataLinkCategory: string;
  dataLinkName?: string;
  hasActiveShowtimes?: boolean;
  hasPresaleShowtimes?: boolean;
  imageClassName?: string;
  index?: number;
  isShowtimesLoading?: boolean;
  movie: MoviePoster;
  movieUrlOverride?: string;
  onTrailerOpenChange?: (open: boolean) => void;
  playButtonClassName?: string;
  posterClassName?: string;
  presaleLinkText?: string;
  presaleLinkUrl?: string;
  presaleMessage?: string;
  publicOnSaleDate?: Date;
  showAdvanceTickets?: boolean;
  showPlayButton?: boolean;
  showtimes?: BasicShowtime[];
  showTitle?: boolean;
  showWatchlistButton?: boolean;
  theaterName?: string;
  timeZone?: string;
  titleClassName?: string;
  titleDataLinkName?: string;
  trailerDataLinkName?: string;
  watchlistButtonClassName?: string;
  watchlistIconClassName?: string;
};

export const MoviePosterComponent = ({
  dataLinkCategory,
  dataLinkName,
  hasActiveShowtimes,
  hasPresaleShowtimes,
  imageClassName,
  index,
  isShowtimesLoading,
  movie,
  movieUrlOverride,
  onTrailerOpenChange,
  playButtonClassName,
  posterClassName,
  presaleLinkText,
  presaleLinkUrl,
  presaleMessage,
  publicOnSaleDate,
  showAdvanceTickets = false,
  showPlayButton = true,
  showtimes,
  showTitle = false,
  showWatchlistButton = true,
  theaterName,
  timeZone,
  titleClassName,
  titleDataLinkName,
  trailerDataLinkName,
  watchlistButtonClassName,
  watchlistIconClassName,
}: MoviePosterComponentProps): React.JSX.Element => {
  const [advanceTickets] = useState<boolean>(hasAdvanceTickets(movie.advanceTicketsStartDate, movie.advanceTicketsEndDate));
  const { trackClick } = useDataAnalytics();
  const movieUrl = movieUrlOverride ?? movie.movieUrl;

  return (
    <div className={cn(showAdvanceTickets || showTitle ? 'grid w-[150px] md:w-[250px]' : 'relative', posterClassName)}>
      {showAdvanceTickets &&
        <Anchor aria-hidden={!advanceTickets}
          className={cn(
            'font-bold bg-[var(--surface)] rounded-bl-none rounded-br-none rounded-tl-md rounded-tr-md',
            !advanceTickets && 'invisible pointer-events-none'
          )}
          data-test="link-advance-tickets-movie"
          href={movieUrl}
          tabIndex={advanceTickets ? undefined : -1}
          variant="secondary"
          onClick={() => trackClick({
            linkCategory: dataLinkCategory,
            linkName: 'Advance Tickets',
          })}
        >
          Advance Tickets
        </Anchor>
      }

      <div className={cn('relative', showAdvanceTickets || showTitle ? 'aspect-2/3' : 'size-full')}>
        <MovieItemComponent className={cn(imageClassName, showAdvanceTickets && advanceTickets && 'rounded-t-none')}
          dataLinkCategory={dataLinkCategory}
          dataLinkName={dataLinkName}
          href={movieUrl}
          movie={movie}
        />

        {showPlayButton && movie.movieTrailerUrl &&
          <TrailerDialogComponent dataLinkCategory={dataLinkCategory}
            dataLinkName={trailerDataLinkName}
            dialogIndex={index}
            hasActiveShowtimes={hasActiveShowtimes}
            hasPresaleShowtimes={hasPresaleShowtimes}
            isShowtimesLoading={isShowtimesLoading}
            movieDataLinkName={dataLinkName}
            moviePoster={movie}
            presaleLinkText={presaleLinkText}
            presaleLinkUrl={presaleLinkUrl}
            presaleMessage={presaleMessage}
            publicOnSaleDate={publicOnSaleDate}
            showtimes={showtimes}
            theaterName={theaterName}
            timeZone={timeZone}
            onOpenChange={onTrailerOpenChange}
          >
            <PlayButton ariaLabel="Play Trailer"
              className={playButtonClassName}
              dataTest="button-play"
            />
          </TrailerDialogComponent>
        }

        {showWatchlistButton &&
          <div className={cn(
            'absolute top-2 right-2 z-10 rounded-full bg-black/25 hover:bg-black/40 transition-colors',
            'flex items-center justify-center h-8 w-8',
            watchlistButtonClassName
          )}
          >
            <WatchListIconComponent className={cn('size-4 fill-none stroke-current text-white [&_use]:fill-current [&_use]:stroke-current',
              watchlistIconClassName)}
            dataLinkCategory={dataLinkCategory}
            movieId={movie.cinemarkMovieId}
            movieTitle={movie.movieTitle ?? ''}

            />
          </div>
        }
      </div>

      {showTitle &&
        <Anchor className={cn('font-bold text-secondary justify-start whitespace-normal items-start mt-sm', titleClassName)}
          data-test={`link-movie-${movie.movieTitle ? dataTestTransformer(movie.movieTitle) : movie.cinemarkMovieId}`}
          href={movieUrl}
          variant="link"
          onClick={() => trackClick({
            linkCategory: dataLinkCategory,
            linkName: titleDataLinkName ?? movie.dataLinkName ?? dataLinkName,
          })}
        >
          <span className="line-clamp-2">{movie.movieTitle}</span>
        </Anchor>
      }
    </div>
  );
};
